import type { DeviceAssignment } from '@/types';

/** Fallback group for devices without a facility or location. */
export const DEFAULT_FACILITY = 'Övrigt';

export interface FacilityGroup {
  name: string;
  assignments: DeviceAssignment[];
}

/**
 * Facility (grouping key) for an assignment. Använder den faktiska anläggningen
 * enheten är kopplad till (ziplink_devices.facility_id). Om enheten inte är
 * kopplad till någon anläggning faller vi tillbaka på enhetens location, och
 * sist på DEFAULT_FACILITY.
 */
export function facilityOf(assignment: DeviceAssignment): string {
  return (
    assignment.device?.facility?.name?.trim() ||
    assignment.device?.location?.trim() ||
    DEFAULT_FACILITY
  );
}

/**
 * Group assignments by facility, alphabetically (sv locale) with the
 * fallback group last. Assignments without a loaded device are skipped.
 */
export function groupByFacility(assignments: DeviceAssignment[]): FacilityGroup[] {
  const map = new Map<string, DeviceAssignment[]>();
  for (const assignment of assignments) {
    if (!assignment.device) continue;
    const name = facilityOf(assignment);
    const group = map.get(name);
    if (group) {
      group.push(assignment);
    } else {
      map.set(name, [assignment]);
    }
  }

  return [...map.entries()]
    .map(([name, grouped]) => ({ name, assignments: grouped }))
    .sort((a, b) => {
      if (a.name === DEFAULT_FACILITY) return 1;
      if (b.name === DEFAULT_FACILITY) return -1;
      return a.name.localeCompare(b.name, 'sv');
    });
}

/** Facility names in display order. */
export function facilityNames(assignments: DeviceAssignment[]): string[] {
  return groupByFacility(assignments).map((g) => g.name);
}
